import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  CanSeeBlackFridayPromoDocument,
  CanSeeModulesMarketplaceDocument,
  CanSelfUpsellDocument,
  GetDatoModulesDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { MetaContext } from 'modules/core/lib/navigation/routes'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { locale as localeMap } from 'modules/marketplace/components/utils/locales'
import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { isModuleRevampEnabled } from 'modules/marketplace_modules/components/utils/isModuleRevampEnabled'

type NavigationParam = Parameters<NavigationRoutesFn>[0]

const ModulesRootPage = React.lazy(
  () => import('modules/marketplace_modules/pages/ModulesRootPage')
)
const ModulesDetailTemplate = React.lazy(
  () => import('modules/marketplace_modules/pages/ModulesDetailTemplate')
)
const ModulesPage = React.lazy(() => import('modules/marketplace_modules/pages/ModulesPage'))
const ModulesPromotionsPage = React.lazy(
  () => import('modules/marketplace_modules/pages/ModulesPromotionsPage')
)
const RevampResourceHeader = React.lazy(
  () => import('modules/marketplace_modules/components/Revamp/ResourceHeader')
)

export const getMarketplaceModulesRoute = (navigation: NavigationParam) => {
  return {
    path: navigation.discover.marketplaceModules.path,
    element: <ModulesRootPage />,

    handle: {
      meta: ({ watchQuery }: MetaContext) => {
        const blackFridayPromoQuery = watchQuery({
          query: CanSeeBlackFridayPromoDocument,
        })

        return blackFridayPromoQuery.map(({ data }) => {
          const canSeePromo = data?.upsell._policies.canSeeBlackFridayPromo.can

          const baseMetadata = {
            title: i18n.t('marketplace.modules.title'),
            description: i18n.t('marketplace.modules.description'),
            illustration: 'boxes' as const,
          }

          if (canSeePromo) {
            return {
              ...baseMetadata,
              subNavigation: [
                {
                  title: i18n.t('marketplace.black_friday_promo.marketplace_list.modules_tab'),
                  href: navigation.discover.marketplaceModules.fullPath(),
                  index: true,
                },
                {
                  title: i18n.t('marketplace.black_friday_promo.marketplace_list.tab'),
                  href: navigation.discover.marketplaceModules.promotion.fullPath(),
                },
              ],
            }
          }

          return baseMetadata
        })
      },
    },

    children: [
      {
        index: true,
        element: <ModulesPage />,
      },
      {
        path: navigation.discover.marketplaceModules.promotion.path,
        element: <ModulesPromotionsPage />,
      },
      {
        path: navigation.discover.marketplaceModules.item.path,
        element: <ModulesDetailTemplate />,

        handle: {
          resource: true,
          meta: ({
            params,
            watchQuery,
            session: {
              company,
              access: { locale: user_locale },
            },
          }: MetaContext) => {
            const slug = params['id']
            const datoFormatLocale = localeMap[user_locale]
            const countryCodeForCompany = countryCode(company.legalEntities[0]?.country || '')
            const modulesQuery = watchQuery({
              query: GetDatoModulesDocument,
              variables: {
                locale: datoFormatLocale,
                slug: slug,
                countryCode: countryCodeForCompany,
                preview: false,
              },
            })

            const moduleAuthQuery = watchQuery({
              query: CanSeeModulesMarketplaceDocument,
            })

            const selfUpsellQuery = watchQuery({
              query: CanSelfUpsellDocument,
            })

            return modulesQuery.flatMap(({ data: moduleData }) => {
              const module = moduleData?.modules.datoModulesConnection.nodes[0]
              const canSeeMarketplaceRevamp = isModuleRevampEnabled(
                module?.chargebeeModuleName || ''
              )

              return moduleAuthQuery.flatMap(({ data: moduleAuthData }) => {
                const canUpsell = moduleAuthData?.modules._policies.moduleManageModule.can

                return selfUpsellQuery.map(({ data: selfUpsellData }) => {
                  const canSubscriptionUpgrade =
                    selfUpsellData?.billing._policies.subscriptionUpgradeSubscription.can

                  const isFreeTrial = company.subscription.inFreeTrial

                  const canSeeSelfUpsell = !isFreeTrial && !!canSubscriptionUpgrade
                  const canSeeNormalUpsell = !isFreeTrial && !!canUpsell && !canSubscriptionUpgrade
                  return {
                    title: module?.name || i18n.t('marketplace.modules.title'),
                    headerComponent: canSeeMarketplaceRevamp && (
                      <Box
                        border={{
                          bottom: {
                            width: 's1',
                            style: 'solid',
                            color: 'grey200',
                          },
                        }}
                      >
                        <RevampResourceHeader
                          moduleData={module}
                          addonReference={module?.billingAddonReference || []}
                          crossSellingAddonReference={
                            module?.crossSellingBillingAddonReference || []
                          }
                          canSeeSelfUpsell={canSeeSelfUpsell}
                          canSeeNormalUpsell={canSeeNormalUpsell}
                        />
                      </Box>
                    ),
                  }
                })
              })
            })
          },
        },
      },
    ],
  }
}
